// Employee self-service requests (Phase 2b) — submit travel/training/
// reimbursement/equipment requests (routed through the approval engine) and set
// out-of-office. Approvals happen in the Approvals inbox (manager/HR).
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Plane } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const KINDS = ['Travel', 'Training', 'Reimbursement', 'Equipment', 'Other'];
const tone = (s) => (s === 'Approved' ? 'success' : s === 'Rejected' ? 'error' : s === 'Pending HR' ? 'info' : 'warning');

export default function Requests({ showToast }) {
  const [rows, setRows] = useState(null);
  const [ooo, setOoo] = useState(null);
  const [form, setForm] = useState(null);
  const [oooForm, setOooForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { api.myRequests().then(setRows).catch(() => setRows([])); api.getOoo().then(setOoo).catch(() => setOoo(null)); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || busy) return; setBusy(true);
    try { await api.submitRequest(form); showToast?.('Request submitted'); setForm(null); await load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const saveOoo = async () => {
    if (!oooForm.from || !oooForm.to || busy) return; setBusy(true);
    try { await api.setOoo(oooForm); showToast?.('Out-of-office set'); setOooForm(null); await load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const clearOoo = async () => { try { await api.clearOoo(); await load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Requests" sub="Travel, training, reimbursement & equipment — routed for approval" />
        <PrimaryBtn onClick={() => setForm({ kind: 'Travel', title: '', details: '', amount: '' })}><Plus size={14} style={{ marginRight: 6 }} /> New request</PrimaryBtn>
      </div>

      {/* Out-of-office */}
      <Card style={{ padding: 16 }} className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div style={{ width: 40, height: 40, borderRadius: 12, background: AG.primaryLightest, color: AG.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plane size={18} /></div>
          <div>
            <div style={{ fontWeight: 600, color: AG.text }}>Out of office</div>
            <div style={{ fontSize: 13, color: AG.n500 }}>{ooo ? `${ooo.from} → ${ooo.to}${ooo.note ? ` · ${ooo.note}` : ''}` : 'Not set'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ooo && <OutlineBtn onClick={clearOoo}>Clear</OutlineBtn>}
          <PrimaryBtn onClick={() => setOooForm(ooo ? { from: ooo.from, to: ooo.to, note: ooo.note || '' } : { from: '', to: '', note: '' })}>{ooo ? 'Edit' : 'Set'}</PrimaryBtn>
        </div>
      </Card>

      {rows.length === 0 ? (
        <Card><EmptyState icon={Plus} title="No requests yet" desc="Submit a travel, training, reimbursement or equipment request." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((r) => (
            <Card key={r.id} style={{ padding: 14 }} className="flex items-center justify-between">
              <div>
                <div style={{ fontWeight: 600, color: AG.text }}>{r.title} <span style={{ color: AG.n400, fontWeight: 400 }}>· {r.kind}{r.amount ? ` · Rs ${Number(r.amount).toLocaleString('en-IN')}` : ''}</span></div>
                <div style={{ fontSize: 12, color: AG.n500 }}>{r.createdAt?.slice(0, 10)}{r.details ? ` · ${r.details}` : ''}</div>
              </div>
              <Pill tone={tone(r.status)} dot>{r.status}</Pill>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New request</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</label>
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>
                {KINDS.map((k) => <option key={k}>{k}</option>)}
              </select>
              <TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Details (optional)" multiline minRows={2} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" type="number" label="Amount Rs (optional)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={submit} disabled={busy}>Submit</PrimaryBtn>
        </DialogActions>
      </Dialog>

      <Dialog open={!!oooForm} onClose={() => !busy && setOooForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Out of office</DialogTitle>
        <DialogContent>
          {oooForm && (
            <div style={{ paddingTop: 6 }} className="grid grid-cols-2 gap-2">
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={oooForm.from} onChange={(e) => setOooForm({ ...oooForm, from: e.target.value })} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={oooForm.to} onChange={(e) => setOooForm({ ...oooForm, to: e.target.value })} />
              <div style={{ gridColumn: '1 / -1' }}><TextField fullWidth size="small" label="Note (optional)" value={oooForm.note} onChange={(e) => setOooForm({ ...oooForm, note: e.target.value })} /></div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OutlineBtn onClick={() => setOooForm(null)} disabled={busy}>Cancel</OutlineBtn>
          <PrimaryBtn onClick={saveOoo} disabled={busy}>Save</PrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}
